/**
 * WebOrdersPageViewModel - Web Platform Adapter
 *
 * Web-specific implementation of OrdersPageViewModel
 * Reads order history from orderStore and transforms to TileOrderHistoryProps
 */

import { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrderStore, useRestaurantStore, ACTIVE_ORDER_STATES, getTotal } from '@restaurant/core'
import type { Order, Location } from '@restaurant/core'
import type { TileOrderHistoryProps } from '@restaurant/core'
import type { PageContentOrdersProps } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'


/**
 * Format currency value
 */
function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`
}

/**
 * Get order total from priceBreakdown using the canonical getTotal utility.
 */
function getOrderTotal(order: Order): number {
  if (!order.priceBreakdown) return 0
  return getTotal(order.priceBreakdown)
}

/**
 * Format date for display
 */
function formatOrderDate(dateString: string): string {
  const date = new Date(dateString)
  return (
    date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }) +
    ' at ' +
    date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  )
}

/**
 * Get location name from merchant_id by looking up in locations
 */
function getLocationName(
  merchantId: string | undefined,
  locations: Location[]
): string {
  if (!merchantId || !locations.length) return 'Restaurant'
  const location = locations.find((loc) => loc.merchantId === merchantId || loc.originalMerchantId === merchantId)
  return location?.name || 'Restaurant'
}

/**
 * Transform Order to TileOrderHistoryProps
 */
function mapOrderToTileProps(
  order: Order,
  locations: Location[]
): TileOrderHistoryProps {
  // Get order number from externalId or fallback to last 6 chars of id
  const orderNumber =
    order.externalId ||
    order.id?.slice(-6).toUpperCase() ||
    'N/A'

  // Derive order type label from isDelivery boolean
  const orderType: 'Pickup' | 'Delivery' = order.isDelivery ? 'Delivery' : 'Pickup'

  // Get location info
  const merchantId = order.merchantId
  const locationName = getLocationName(merchantId, locations)

  // Get delivery address if applicable
  const locationAddress = order.isDelivery
    ? order.deliveryAddress?.street
    : undefined

  // Get order date from orderTime
  const orderDate = order.orderTime

  return {
    orderNumber,
    orderCost: formatCurrency(getOrderTotal(order)),
    orderType,
    locationName,
    locationAddress,
    orderDate: orderDate ? formatOrderDate(orderDate) : 'Date unavailable',
  }
}

/**
 * Web OrdersPageViewModel Hook
 *
 * Usage:
 * ```tsx
 * import { useWebOrdersPageViewModel } from './WebOrdersPageViewModel'
 *
 * function OrdersPage() {
 *   const viewModel = useWebOrdersPageViewModel()
 *   const props = viewModel.getPageContentOrdersProps()
 *
 *   return <PageContentOrders {...props} />
 * }
 * ```
 */
export const useWebOrdersPageViewModel = () => {
  const navigate = useNavigate()

  // Get order data from store
  const { orderHistory, isLoadingHistory } = useOrderStore()
  const setTrackingSheetOpen = useOrderStore((s) => s.setTrackingSheetOpen)
  const { locations } = useRestaurantStore()

  // Transform orders to tile props
  // orderHistory from store is already filtered (no draft orders)
  const orders = useMemo(() => {
    return orderHistory.map((order) => mapOrderToTileProps(order, locations))
  }, [orderHistory, locations])

  // Handle order tile click — opens tracking sheet for active orders
  const handleOrderClick = useCallback(
    (tileProps: TileOrderHistoryProps, index: number) => {
      safeGetLogger()?.order('tracked', { orderNumber: tileProps.orderNumber, index })
      const order = orderHistory[index]
      if (order && ACTIVE_ORDER_STATES.includes(order.status)) {
        setTrackingSheetOpen(true)
      }
    },
    [orderHistory, setTrackingSheetOpen]
  )

  // Handle details button click — opens tracking sheet for active orders, navigates to details for past orders
  const handleDetailsPress = useCallback(
    (_tileProps: TileOrderHistoryProps, index: number) => {
      const order = orderHistory[index]
      if (!order) return

      if (ACTIVE_ORDER_STATES.includes(order.status)) {
        setTrackingSheetOpen(true)
        return
      }

      navigate(`/orders/${order.id}/details`)
    },
    [orderHistory, setTrackingSheetOpen, navigate]
  )

  // Handle re-order button click — navigates to reorder page
  const handleReorderPress = useCallback(
    (_tileProps: TileOrderHistoryProps, index: number) => {
      const order = orderHistory[index]
      if (!order) return
      navigate(`/reorder/${order.id}`)
    },
    [orderHistory, navigate]
  )

  // Get props for PageContentOrders
  const getPageContentOrdersProps = useCallback((): Partial<PageContentOrdersProps> => {
    return {
      orders,
      loading: isLoadingHistory,
      onOrderClick: handleOrderClick,
      onDetailsPress: handleDetailsPress,
      onReorderPress: handleReorderPress,
    }
  }, [orders, isLoadingHistory, handleOrderClick, handleDetailsPress, handleReorderPress])

  return {
    orders,
    loading: isLoadingHistory,
    handleOrderClick,
    handleDetailsPress,
    handleReorderPress,
    getPageContentOrdersProps,
  }
}

export default useWebOrdersPageViewModel
